import { useState, useCallback, useEffect } from 'react';
import api from '../../services/api';
import { API_ROUTES } from '../../services/apiRoutes';

export const useControlDocuments = (token, dataroomId, controlId) => {
    const [documentReferences, setDocumentReferences] = useState(null);
    const [availableDocuments, setAvailableDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [availableDocumentsLoading, setAvailableDocumentsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchReferences = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await api.get(
                API_ROUTES.GET_CONTROL_REFERENCES(dataroomId, controlId),
                {
                    headers: { 
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setDocumentReferences(response.data.data.references);
            setLoading(false);
            return response.data.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while fetching document references';
            setError(errorMessage);
            setLoading(false);
        }
    }, [token, dataroomId, controlId]);

    const createReference = useCallback(async (reference_description, reference_name = '') => {
        setLoading(true);
        setError(null);

        try {
            await api.post(
                API_ROUTES.CREATE_CONTROL_REFERENCE(dataroomId, controlId),
                {
                    reference_description,
                    reference_name
                },
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            await fetchReferences();
            return true;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while creating the reference';
            setError(errorMessage);
            setLoading(false);
            throw new Error(errorMessage);
        }
    }, [token, dataroomId, controlId, fetchReferences]);

    const deleteReference = useCallback(async (controlId, referenceId, deleteCompletely = false) => {
        setLoading(true);
        setError(null);
    
        try {
            await api.delete(
                API_ROUTES.DELETE_CONTROL_REFERENCE(dataroomId, controlId, referenceId),
                {
                    headers: { 
                        Authorization: `Bearer ${token}`
                    },
                    data: {
                        delete_completely: deleteCompletely
                    }
                }
            );
    
            await fetchReferences();
            return true;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while deleting the reference';
            setError(errorMessage);
            setLoading(false);
            throw new Error(errorMessage);
        }
    }, [token, dataroomId, fetchReferences]);

    const sendReference = useCallback(async (controlId, referenceId) => {
        setLoading(true);
        setError(null);

        try {
            await api.post(
                API_ROUTES.SEND_CONTROL_REFERENCE(dataroomId, controlId, referenceId),
                {}, 
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            await fetchReferences();
            return true;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while sending the reference';
            setError(errorMessage);
            setLoading(false);
            throw new Error(errorMessage);
        }
    }, [token, dataroomId, fetchReferences]);

    const extractReferences = useCallback(async (userRequest) => {
        setLoading(true);
        setError(null);

        try {
            const response = await api.post(
                API_ROUTES.EXTRACT_FEC_REFERENCES(dataroomId, controlId),
                { user_request: userRequest },
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            await fetchReferences();
            return response.data.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while extracting document references';
            setError(errorMessage);
            setLoading(false);
            throw new Error(errorMessage);
        }
    }, [token, dataroomId, controlId, fetchReferences]);

    const updateAttribute = useCallback(async (dataroomId, attributeId, newValue) => {
        setLoading(true);
        setError(null);
    
        try {
            await api.patch(
                API_ROUTES.UPDATE_ATTRIBUTE(dataroomId, attributeId),
                {
                    value: newValue
                },
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
    
            await fetchReferences();
            return true;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while updating the attribute';
            setError(errorMessage);
            setLoading(false);
            throw new Error(errorMessage);
        }
    }, [token, fetchReferences]);

    const fetchAvailableDocuments = useCallback(async () => {
        setAvailableDocumentsLoading(true); // Use the new loading state
        setError(null);
    
        try {
            const response = await api.get(
                API_ROUTES.GET_AVAILABLE_DOCUMENTS(dataroomId, controlId),
                {
                    headers: { 
                        Authorization: `Bearer ${token}`
                    }
                }
            );
    
            setAvailableDocuments(response.data.data.documents);
            setAvailableDocumentsLoading(false);
            return response.data.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while fetching available documents';
            setError(errorMessage);
            setAvailableDocumentsLoading(false);
        }
    }, [token, dataroomId, controlId]);
    
    // Nouvelle fonction pour lier un document
    const linkExistingDocument = useCallback(async (documentId) => {
        setLoading(true);
        setError(null);
    
        try {
            await api.post(
                API_ROUTES.LINK_EXISTING_DOCUMENT(dataroomId, controlId, documentId),
                {},
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
    
            await fetchReferences();
            await fetchAvailableDocuments();
            return true;
        } catch (error) {
            console.log(error);
            const errorMessage = error.response?.data?.message || 'An error occurred while linking the document';
            setError(errorMessage);
            setLoading(false);
            throw new Error(errorMessage);
        }
    }, [token, dataroomId, controlId, fetchReferences, fetchAvailableDocuments]);

    const linkMultipleDocuments = useCallback(async (documentIds) => {
        setLoading(true);
        setError(null);
    
        try {
            await api.post(
                API_ROUTES.LINK_MULTIPLE_DOCUMENTS(dataroomId, controlId),
                {
                    document_ids: documentIds
                },
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
    
            await fetchReferences();
            await fetchAvailableDocuments();
            return true;
        } catch (error) {
            console.error(error);
            const errorMessage = error.response?.data?.message || 'An error occurred while linking the documents';
            setError(errorMessage);
            setLoading(false);
            throw new Error(errorMessage);
        }
    }, [token, dataroomId, controlId, fetchReferences, fetchAvailableDocuments]);

    const createAndLinkMixedReferences = useCallback(async ({ newReferences, existingReferenceIds }) => {
        setLoading(true);
        setError(null);
    
        try {
            await api.post(
                API_ROUTES.CREATE_AND_LINK_REFERENCES(dataroomId, controlId),
                {
                    new_references: newReferences,
                    existing_reference_ids: existingReferenceIds
                },
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
    
            // Rafraîchir les données après l'opération
            await fetchReferences();
            await fetchAvailableDocuments();
            return true;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'An error occurred while creating and linking references';
            setError(errorMessage);
            setLoading(false);
            throw new Error(errorMessage);
        }
    }, [token, dataroomId, controlId, fetchReferences, fetchAvailableDocuments]);

    const clearReferences = useCallback(() => {
        setDocumentReferences(null);
        setError(null);
    }, []);

    useEffect(() => {
        fetchReferences();
        fetchAvailableDocuments();
    }, [fetchReferences, fetchAvailableDocuments]);

    return {
        documentReferences,
        loading,
        availableDocumentsLoading,
        error,
        extractReferences,
        clearReferences,
        fetchReferences,
        deleteReference,
        sendReference,
        createReference,
        updateAttribute,
        availableDocuments,
        fetchAvailableDocuments,
        linkExistingDocument,
        linkMultipleDocuments,
        createAndLinkMixedReferences
    };
};